/**
 * League fight reminders (docs/game-design.md §7.4, D56): this browser's notifications (Web Push),
 * Telegram, email and the league calendar, as rows in Settings and in a sheet that the league
 * fight cards open (Home, League).
 */
import { LEAGUES } from '@caged/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useReminders } from '../../state/reminders';
import { useClockTime } from '../league';
import { usePush } from '../usePush';
import s from '../screens/SettingsScreen.module.css';
import { Switch } from './Controls';
import { Modal } from './Modal';
import r from './Reminders.module.css';
import { Row } from './SettingRow';
import ui from './ui.module.css';

/** A link to the bot lasts 30 minutes on the server: a fresh one well before. */
const LINK_REFRESH_MS = 20 * 60_000;

function PushRow() {
  const { t } = useTranslation();
  const push = usePush();
  const id = useId();
  const hint =
    push.availability === 'installFirst'
      ? t('screens.settings.remindersInstall')
      : push.availability === 'no'
        ? t('screens.settings.remindersNo')
        : push.problem === 'denied'
          ? t('screens.settings.remindersDenied')
          : push.problem
            ? t('screens.settings.remindersFailed')
            : t('screens.settings.remindersHint');
  return (
    <Row label={t('screens.settings.remindersLabel')} hint={hint} htmlFor={id}>
      {push.availability === 'yes' && <Switch id={id} checked={push.enabled} onChange={(v) => void push.set(v)} label={t('screens.settings.remindersLabel')} />}
    </Row>
  );
}

/**
 * Telegram: Connect opens the bot with a one-time link (ready before the tap, so it opens as a
 * plain link that no pop-up blocker stops); pressing Start there connects it.
 */
function TelegramRow() {
  const { t, i18n } = useTranslation();
  const telegram = useReminders((st) => st.telegram);
  const watchTelegram = useReminders((st) => st.watchTelegram);
  const disconnectTelegram = useReminders((st) => st.disconnectTelegram);
  const [url, setUrl] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const wanted = !!telegram?.available && !telegram.linked;
  useEffect(() => {
    if (!wanted) return;
    let alive = true;
    const fetchLink = () =>
      void import('../../net/remindersApi')
        .then((api) => api.telegramLinkRemote(i18n.language))
        .then(
          (u) => alive && setUrl(u),
          () => alive && setUrl(null),
        );
    fetchLink();
    const id = setInterval(fetchLink, LINK_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [wanted, i18n.language]);
  if (!telegram?.available) return null;

  const disconnect = async () => {
    setBusy(true);
    setFailed(false);
    const ok = await disconnectTelegram();
    setBusy(false);
    setOpened(false);
    if (!ok) setFailed(true);
  };
  const hint = telegram.linked ? t('screens.settings.telegramOn') : opened ? t('screens.settings.telegramWaiting') : t('screens.settings.telegramHint');
  return (
    <>
      <Row label={t('screens.settings.telegramLabel')} hint={hint}>
        {telegram.linked ? (
          <button type="button" className={[ui.ghost, s.dangerBtn].join(' ')} onClick={() => void disconnect()} disabled={busy}>
            {t('screens.settings.telegramDisconnect')}
          </button>
        ) : (
          <a
            className={[ui.ghost, s.dangerBtn].join(' ')}
            href={url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!url}
            onClick={(e) => {
              if (!url) return e.preventDefault();
              setOpened(true);
              watchTelegram();
            }}
          >
            {t('screens.settings.telegramConnect')}
          </a>
        )}
      </Row>
      {failed && (
        <p className={s.rowAlert} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </>
  );
}

/** Email, to the account's address: for a registered player with one (a Facebook sign-in may have none). */
function EmailRow() {
  const { t, i18n } = useTranslation();
  const email = useReminders((st) => st.email);
  const setEmail = useReminders((st) => st.setEmail);
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!email?.available || !email.address) return null;
  const toggle = async (on: boolean) => {
    setBusy(true);
    setFailed(false);
    if (!(await setEmail(on, i18n.language))) setFailed(true);
    setBusy(false);
  };
  return (
    <>
      <Row label={t('screens.settings.emailLabel')} hint={t('screens.settings.emailHint', { address: email.address })} htmlFor={id}>
        <Switch id={id} checked={email.on} onChange={(v) => !busy && void toggle(v)} label={t('screens.settings.emailLabel')} />
      </Row>
      {failed && (
        <p className={s.rowAlert} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </>
  );
}

/**
 * The league calendar: Add makes the player's private feed link; then it goes to Apple Calendar
 * (iPhone, Mac: webcal://), Google Calendar (its subscribe page) or anywhere else as a copied link.
 * The calendar fetches the fights itself, so they stay up to date.
 */
function CalendarRow() {
  const { t, i18n } = useTranslation();
  const calendar = useReminders((st) => st.calendar);
  const makeCalendar = useReminders((st) => st.makeCalendar);
  const removeCalendar = useReminders((st) => st.removeCalendar);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!calendar?.available) return null;
  const run = async (action: () => Promise<boolean>) => {
    setBusy(true);
    setFailed(false);
    if (!(await action())) setFailed(true);
    setBusy(false);
  };
  const url = calendar.url;
  const webcal = url?.replace(/^https?:/, 'webcal:') ?? '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFailed(true);
    }
  };
  return (
    <>
      <Row label={t('screens.settings.calendarLabel')} hint={url ? t('screens.settings.calendarReady') : t('screens.settings.calendarHint')}>
        <button
          type="button"
          className={[ui.ghost, s.dangerBtn].join(' ')}
          disabled={busy}
          onClick={() => void run(url ? removeCalendar : () => makeCalendar(i18n.language))}
        >
          {url ? t('screens.settings.calendarRemove') : t('screens.settings.calendarAdd')}
        </button>
      </Row>
      {url && (
        <div className={r.calendarOptions}>
          <a className={[ui.ghost, s.dangerBtn].join(' ')} href={webcal}>
            {t('screens.settings.calendarApple')}
          </a>
          <a className={[ui.ghost, s.dangerBtn].join(' ')} href={`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`} target="_blank" rel="noopener noreferrer">
            {t('screens.settings.calendarGoogle')}
          </a>
          <button type="button" className={[ui.ghost, s.dangerBtn].join(' ')} onClick={() => void copy()}>
            {copied ? t('screens.settings.calendarCopied') : t('screens.settings.calendarCopy')}
          </button>
        </div>
      )}
      {failed && (
        <p className={s.rowAlert} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </>
  );
}

/** When reminders come: at the player's league fight time, when its live window opens (D56). */
function When() {
  const { t } = useTranslation();
  const mine = useLeague((st) => st.mine);
  const time = useClockTime();
  useEffect(() => {
    void useLeague.getState().load();
  }, []);
  const minutes = LEAGUES.windowMinutes;
  return (
    <p className={s.remindersWhen}>
      {mine?.status === 'ok' ? t('screens.settings.remindersWhen', { time: time(mine.next.fightAt), minutes }) : t('screens.settings.remindersWhenAny', { minutes })}
    </p>
  );
}

/** Every way to be reminded, each as a row, after when they come. */
export function ReminderRows() {
  const load = useReminders((st) => st.load);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <>
      <When />
      <PushRow />
      <TelegramRow />
      <EmailRow />
      <CalendarRow />
    </>
  );
}

/** From the League's fight card: the same choices as in Settings. */
export function RemindersSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <Modal onClose={onClose} labelledBy={titleId} variant="sheet">
      <div className={s.dialog}>
        <span className={ui.label}>{t('screens.settings.league')}</span>
        <h2 id={titleId} className={s.dialogTitle}>
          {t('screens.league.remindersTitle')}
        </h2>
        <div>
          <ReminderRows />
        </div>
        <button type="button" className={ui.ghost} onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * On the league fight cards (Home, League), so nobody has to find Settings: "Remind me at fight
 * time" while no reminder is on, else when it comes. Either opens the choices. Nothing when this
 * player can't get any reminder here.
 */
export function ReminderPrompt({ fightAt }: { fightAt: number }) {
  const { t } = useTranslation();
  const push = usePush();
  const telegram = useReminders((st) => st.telegram);
  const email = useReminders((st) => st.email);
  const load = useReminders((st) => st.load);
  const time = useClockTime();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    void load();
  }, [load]);
  const on = push.enabled || !!telegram?.linked || !!email?.on;
  if (!on && push.availability === 'no' && !telegram?.available && !email?.available) return null;
  return (
    <>
      {on ? (
        <button type="button" className={r.on} onClick={() => setOpen(true)}>
          <span aria-hidden="true">🔔</span>
          {t('screens.league.remindedAt', { time: time(fightAt) })}
        </button>
      ) : (
        <button type="button" className={[ui.secondary, r.prompt].join(' ')} onClick={() => setOpen(true)}>
          {t('screens.league.remindMe')}
          <span aria-hidden="true">🔔</span>
        </button>
      )}
      {open && <RemindersSheet onClose={() => setOpen(false)} />}
    </>
  );
}
